import React, { useCallback, useMemo } from "react";
import { Avatar, Box, Link as MuiLink, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import InfoActionIcons from "../InfoActionIcons";
import { buildOpenCompanyNavPayload } from "../../utils/companyNavigation";
import { buildCompanyProfileActions } from "../../utils/companySocialLinks";

export default function JobTabHeader({
  companyLogo,
  companyName,
  companyDomain,
  actions = [],
  profileData = null,
  companyDefaultTab = "jobs",
  sx = {},
}) {
  const resolvedActions = useMemo(() => {
    if (Array.isArray(actions) && actions.length) return actions;
    if (!profileData || typeof profileData !== "object") return [];

    return buildCompanyProfileActions({
      website:
        profileData?.companyWebsite ||
        profileData?.careerWebsite ||
        profileData?.leadLink ||
        profileData?.otherLink ||
        profileData?.companyDomain ||
        "",
      contact_email:
        profileData?.primaryContactEmail ||
        profileData?.companyEmail ||
        profileData?.contact_email ||
        profileData?.email ||
        "",
      contact_phone:
        profileData?.primaryContactPhone ||
        profileData?.companyPhone ||
        profileData?.contact_phone ||
        profileData?.phone ||
        "",
      linkedin_url:
        profileData?.primaryContactLinkedin ||
        profileData?.linkedinUrl ||
        profileData?.linkedin_url ||
        "",
      facebook_url: profileData?.facebookUrl || profileData?.facebook_url || "",
      twitter_url:
        profileData?.twitterUrl ||
        profileData?.twitter_url ||
        profileData?.x_url ||
        "",
      google_url: profileData?.googleUrl || profileData?.google_url || "",
      instagram_url: profileData?.instagramUrl || profileData?.instagram_url || "",
      youtube_url: profileData?.youtubeUrl || profileData?.youtube_url || "",
      tiktok_url: profileData?.tiktokUrl || profileData?.tiktok_url || "",
      social_links: profileData?.socialLinks || profileData?.social_links || [],
    });
  }, [actions, profileData]);

  const navigate = useNavigate();

  const handleOpenCompany = useCallback(() => {
    const companyId = Number(
      profileData?.companyId || profileData?.company_id || profileData?.id || 0
    );
    if (!Number.isFinite(companyId) || companyId <= 0) return;

    const resolvedCompanyName = String(
      companyName ||
        profileData?.companyName ||
        profileData?.company_name ||
        profileData?.name ||
        "Company"
    ).trim() || "Company";

    const logo = companyLogo || profileData?.companyLogo || profileData?.company_logo || "";
    const website =
      profileData?.companyWebsite || profileData?.careerWebsite || profileData?.companyDomain || "";

    const payload = buildOpenCompanyNavPayload({
      company: {
        id: companyId,
        company_id: companyId,
        companyId,
        name: resolvedCompanyName,
        company_name: resolvedCompanyName,
        logo,
        company_logo: logo,
        website,
        website_url: website,
        company_website: website,
      },
      defaultTab: companyDefaultTab,
      returnListTab: "companies",
      entityType: "company",
    });

    if (!payload) return;

    try {
      sessionStorage.setItem("rpo:openCompanyNav", JSON.stringify(payload));
    } catch {
      // Ignore session storage failures.
    }

    navigate(
      {
        pathname: "/companies",
        search: `?companyId=${encodeURIComponent(String(companyId))}&tab=${encodeURIComponent(
          companyDefaultTab
        )}&entityType=company&returnTab=companies`,
      },
      {
        state: payload,
      }
    );
  }, [companyDefaultTab, companyLogo, companyName, navigate, profileData]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mb: 2,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <Avatar
          src={companyLogo}
          sx={{
            width: 42,
            height: 42,
            background: "radial-gradient(circle at 30% 30%, #a855f7, #6366f1)",
            flex: "0 0 auto",
          }}
        >
          {(companyName || "?")?.[0]}
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          {profileData?.companyId || profileData?.company_id || profileData?.id ? (
            <MuiLink
              component="button"
              type="button"
              onClick={handleOpenCompany}
              underline="hover"
              sx={{
                display: "inline-block",
                border: 0,
                padding: 0,
                background: "transparent",
                textAlign: "left",
                fontFamily: "Be Vietnam Pro",
                fontWeight: 600,
                fontSize: 14,
                color: "#111827",
                lineHeight: 1.2,
                cursor: "pointer",
                maxWidth: "100%",
              }}
              noWrap
            >
              {companyName || "-"}
            </MuiLink>
          ) : (
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontWeight: 600,
                fontSize: 14,
                color: "#111827",
                lineHeight: 1.2,
              }}
              noWrap
            >
              {companyName || "-"}
            </Typography>
          )}

          <Typography
            sx={{
              mt: 0.25,
              fontFamily: "Be Vietnam Pro",
              fontSize: 14,
              fontWeight: 500,
              color: "#9C4FFF",
              lineHeight: "24px",
            }}
            noWrap
          >
            {companyDomain || "-"}
          </Typography>
        </Box>

        {resolvedActions.length ? (
          <Box
            aria-hidden="true"
            sx={{
              width: "1px",
              height: 20,
              backgroundColor: "#CBD5E1",
              flex: "0 0 auto",
            }}
          />
        ) : null}

        {resolvedActions.length ? (
          <InfoActionIcons
            actions={resolvedActions}
            gap={0.9}
            preserveOrder={true}
            showGroupDivider={false}
          />
        ) : null}
      </Box>
      {profileData?.scheduleActionTargetRef && (
        <Box ref={profileData.scheduleActionTargetRef} sx={{ ml: "auto", flexShrink: 0, alignSelf: "center" }} />
      )}
    </Box>
  );
}
